import { _setRecordingCoordinator, breezeEngineRecording } from '../sdk/useRecordingApi';
const NOOP = () => {};
const noopLogger = {
  dispatch: NOOP,
  dispatchNoop: NOOP,
  transportSend: NOOP,
  transportSendResponse: NOOP,
  transportResume: NOOP,
  transportResumeResponse: NOOP,
  transportToolResponse: NOOP,
  transportToolResponseResponse: NOOP,
  transportCancel: NOOP,
  transportStreamEvent: NOOP,
  ablyRawEvent: NOOP,
  transportReconnect: NOOP,
  transportRecovery: NOOP,
  transportPollCycle: NOOP,
  transportError: NOOP,
  toolExecutionStart: NOOP,
  toolExecutionEnd: NOOP,
  conversationLoad: NOOP,
  conversationLoaded: NOOP,
  ablyConnectionSend: NOOP,
  ablyEnsureConnected: NOOP,
  ablyConnected: NOOP,
  ablyConnectFailed: NOOP,
  ablyDetachFailed: NOOP,
  ablySubscribed: NOOP,
  ablyMessage: NOOP,
  clientDestroyed: NOOP,
  recordingStart: NOOP,
  streamingRawEvent: NOOP,
  streamingStreamEnd: NOOP
};
// The dev console logger and the recording logger are opt-in via localStorage
// flags. When neither is enabled this is a no-op. Metrics are a separate
// concern (see ChatTelemetry) so this stays logs-only.
export function createChatLogger({
  coordinator,
  initialKey,
  transportMode
} = {}) {
  let log = false;
  let record = false;
  try {
    log = localStorage.getItem('breeze-engine:log') === 'true';
    record = localStorage.getItem('breeze-engine:record') === 'true';
  } catch (_unused) {
    return noopLogger;
  }
  const parts = [];
  if (log) parts.push(createEnabledLogger());
  if (record) parts.push(createRecordingLogger(coordinator, initialKey, transportMode));
  return mergeLoggers(parts);
}

// Composes N loggers into one, skipping no-op parts. A single real part is
// returned as-is so the common path (one active logger) stays single-dispatch.
function mergeLoggers(parts) {
  const active = parts.filter(part => part !== noopLogger);
  if (active.length === 0) return noopLogger;
  if (active.length === 1) return active[0];
  const keys = Object.keys(noopLogger);
  return Object.fromEntries(keys.map(key => [key, (...args) => {
    active.forEach(part => part[key](...args));
  }]));
}
function formatTimestamp() {
  const now = new Date();
  return `${now.toLocaleTimeString('en-US', {
    hour12: false
  })}.${String(now.getMilliseconds()).padStart(3, '0')}`;
}
function logGroup(label, data) {
  console.groupCollapsed(`[breeze-engine] ${label}    ${formatTimestamp()}`);
  for (const [key, value] of Object.entries(data)) {
    console.log(`${key}:`, value);
  }
  console.groupEnd();
}
function resolveLoggerWindow() {
  let top = window;
  let current = window;
  while (current.parent && current.parent !== current) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      current.parent.document;
      top = current.parent;
    } catch (_unused2) {
      break;
    }
    current = current.parent;
  }
  return top;
}
let sharedEntries = null;
let globalWindowProxy = null;
function initBreezeEngineWindow() {
  const loggerWin = resolveLoggerWindow();
  if (globalWindowProxy && loggerWin.__breezeEngine === globalWindowProxy) {
    return;
  }
  const proxy = {
    export() {
      var _sharedEntries;
      return JSON.stringify((_sharedEntries = sharedEntries) !== null && _sharedEntries !== void 0 ? _sharedEntries : [], null, 2);
    },
    exportRecording(conversationId) {
      return breezeEngineRecording.exportRecording(conversationId);
    },
    downloadRecording(conversationId) {
      breezeEngineRecording.downloadRecording(conversationId);
    },
    resetRecording(conversationId) {
      breezeEngineRecording.resetRecording(conversationId);
    },
    hasRecording(conversationId) {
      return breezeEngineRecording.hasRecording(conversationId);
    }
  };
  globalWindowProxy = proxy;
  loggerWin.__breezeEngine = proxy;
  loggerWin.__breezeEngineLogs = proxy;
}
export function createRecordingCoordinator() {
  const registry = new Map();
  let activeKey = null;
  function getThread(conversationId) {
    var _registry$get, _registry$get2;
    return conversationId !== null && conversationId !== undefined ? (_registry$get = registry.get(conversationId)) !== null && _registry$get !== void 0 ? _registry$get : null : (_registry$get2 = registry.get(activeKey)) !== null && _registry$get2 !== void 0 ? _registry$get2 : null;
  }
  const breezeEngine = {
    export() {
      var _sharedEntries2;
      return JSON.stringify((_sharedEntries2 = sharedEntries) !== null && _sharedEntries2 !== void 0 ? _sharedEntries2 : [], null, 2);
    },
    exportRecording(conversationId) {
      var _getThread$exportReco, _getThread;
      return (_getThread$exportReco = (_getThread = getThread(conversationId)) === null || _getThread === void 0 ? void 0 : _getThread.exportRecording()) !== null && _getThread$exportReco !== void 0 ? _getThread$exportReco : null;
    },
    downloadRecording(conversationId) {
      const api = getThread(conversationId);
      if (!api) {
        console.warn('[breeze-engine] No active recording to download');
        return;
      }
      api.downloadRecording();
    },
    resetRecording(conversationId) {
      var _getThread2;
      (_getThread2 = getThread(conversationId)) === null || _getThread2 === void 0 || _getThread2.resetRecording();
    },
    hasRecording(conversationId) {
      var _getThread$hasRecordi, _getThread3;
      return (_getThread$hasRecordi = (_getThread3 = getThread(conversationId)) === null || _getThread3 === void 0 ? void 0 : _getThread3.hasRecording()) !== null && _getThread$hasRecordi !== void 0 ? _getThread$hasRecordi : false;
    }
  };
  _setRecordingCoordinator(breezeEngine);
  initBreezeEngineWindow();
  return {
    register(key, api) {
      registry.set(key, api);
    },
    rekey(oldKey, newKey) {
      const api = registry.get(oldKey);
      if (api) {
        registry.delete(oldKey);
        registry.set(newKey, api);
        if (activeKey === oldKey) {
          activeKey = newKey;
        }
      }
    },
    unregister(key) {
      registry.delete(key);
      if (activeKey === key) {
        activeKey = null;
      }
    },
    setActive(key) {
      activeKey = key;
    }
  };
}
function getOrCreateSharedEntries() {
  var _win$__breezeEngineLo;
  const win = resolveLoggerWindow();
  if (!sharedEntries || !win.__breezeEngineLogs) {
    sharedEntries = [];
  }
  // Only set the minimal BreezeEngineLogs shape when no full-shape object (coordinator proxy or standalone logger) owns __breezeEngineLogs.
  if (!((_win$__breezeEngineLo = win.__breezeEngineLogs) !== null && _win$__breezeEngineLo !== void 0 && _win$__breezeEngineLo.exportRecording)) {
    win.__breezeEngineLogs = {
      export() {
        return JSON.stringify(sharedEntries, null, 2);
      }
    };
  }
  return sharedEntries;
}
function createEnabledLogger() {
  const entries = getOrCreateSharedEntries();
  const toolStartTimes = new Map();
  function appendEntry(entry) {
    entries.push(Object.assign({}, entry, {
      timestamp: new Date().toISOString()
    }));
  }
  return {
    dispatch(action, prevState, nextState) {
      const statusChanged = prevState.status !== nextState.status;
      const statusLabel = statusChanged ? `${prevState.status} -> ${nextState.status}` : nextState.status;
      const label = `${action.type}  ${statusLabel}`;
      logGroup(label, {
        action,
        prevStatus: prevState.status,
        nextStatus: nextState.status,
        conversationId: nextState.conversationId,
        turnId: nextState.turnId
      });
      appendEntry({
        action: 'dispatch',
        label,
        conversationId: nextState.conversationId,
        turnId: nextState.turnId,
        payload: {
          action,
          prevStatus: prevState.status,
          nextStatus: nextState.status,
          conversationId: nextState.conversationId,
          turnId: nextState.turnId
        }
      });
    },
    dispatchNoop(action) {
      const label = `${action.type} (noop)`;
      logGroup(label, {
        action
      });
      appendEntry({
        action: 'dispatch:noop',
        label,
        conversationId: null,
        turnId: null,
        payload: {
          action
        }
      });
    },
    transportSend(request) {
      var _request$conversation;
      const label = `transport:send  agentId=${request.agentId}`;
      logGroup(label, {
        request
      });
      appendEntry({
        action: 'transport:send',
        label,
        conversationId: (_request$conversation = request.conversationId) !== null && _request$conversation !== void 0 ? _request$conversation : null,
        turnId: null,
        payload: {
          request
        }
      });
    },
    transportSendResponse(request, response) {
      const label = `transport:send-response  conversationId=${response.conversationId}  turnId=${response.turnId}`;
      logGroup(label, {
        request,
        response
      });
      appendEntry({
        action: 'transport:send-response',
        label,
        conversationId: response.conversationId,
        turnId: response.turnId,
        payload: {
          request,
          response
        }
      });
    },
    transportResume(request) {
      const label = `transport:resume  conversationId=${request.conversationId}`;
      logGroup(label, {
        request
      });
      appendEntry({
        action: 'transport:resume',
        label,
        conversationId: request.conversationId,
        turnId: request.turnId,
        payload: {
          request
        }
      });
    },
    transportResumeResponse(request, response) {
      const label = `transport:resume-response  conversationId=${response.conversationId}`;
      logGroup(label, {
        request,
        response
      });
      appendEntry({
        action: 'transport:resume-response',
        label,
        conversationId: response.conversationId,
        turnId: request.turnId,
        payload: {
          request,
          response
        }
      });
    },
    transportToolResponse(request) {
      const label = `transport:tool-response  conversationId=${request.conversationId}`;
      logGroup(label, {
        request
      });
      appendEntry({
        action: 'transport:tool-response',
        label,
        conversationId: request.conversationId,
        turnId: request.turnId,
        payload: {
          request
        }
      });
    },
    transportToolResponseResponse(request, __response) {
      const label = `transport:tool-response-response  conversationId=${request.conversationId}`;
      logGroup(label, {
        request
      });
      appendEntry({
        action: 'transport:tool-response-response',
        label,
        conversationId: request.conversationId,
        turnId: request.turnId,
        payload: {
          request
        }
      });
    },
    transportCancel(request) {
      const label = `transport:cancel  conversationId=${request.conversationId}`;
      logGroup(label, {
        request
      });
      appendEntry({
        action: 'transport:cancel',
        label,
        conversationId: request.conversationId,
        turnId: request.turnId,
        payload: {
          request
        }
      });
    },
    transportStreamEvent(event) {
      const label = 'transport:stream-event';
      logGroup(label, {
        event
      });
      appendEntry({
        action: 'transport:stream-event',
        label,
        conversationId: null,
        turnId: null,
        payload: {
          event
        }
      });
    },
    ablyRawEvent(event) {
      const label = 'ably:raw-event';
      logGroup(label, {
        event
      });
      appendEntry({
        action: 'ably:raw-event',
        label,
        conversationId: null,
        turnId: null,
        payload: {
          event
        }
      });
    },
    transportReconnect(conversationId, turnId) {
      const label = `transport:reconnect  conversationId=${conversationId}  turnId=${turnId}`;
      logGroup(label, {
        conversationId,
        turnId
      });
      appendEntry({
        action: 'transport:reconnect',
        label,
        conversationId,
        turnId,
        payload: {
          conversationId,
          turnId
        }
      });
    },
    transportRecovery(outcome, context) {
      const label = `transport:recovery  outcome=${outcome}  stopReason=${context.stopReason}`;
      logGroup(label, context);
      appendEntry({
        action: 'transport:recovery',
        label,
        conversationId: context.conversationId,
        turnId: context.turnId,
        payload: Object.assign({
          outcome
        }, context)
      });
    },
    transportPollCycle(interval, consecutiveErrors) {
      const label = `transport:poll  interval=${interval}ms  errors=${consecutiveErrors}`;
      logGroup(label, {
        interval,
        consecutiveErrors
      });
      appendEntry({
        action: 'transport:poll',
        label,
        conversationId: null,
        turnId: null,
        payload: {
          interval,
          consecutiveErrors
        }
      });
    },
    transportError(error) {
      const label = `transport:error  ${String(error.type)}: ${error.message}`;
      logGroup(label, {
        error
      });
      appendEntry({
        action: 'transport:error',
        label,
        conversationId: null,
        turnId: null,
        payload: {
          error
        }
      });
    },
    toolExecutionStart(toolUseId, toolName, input) {
      toolStartTimes.set(toolUseId, performance.now());
      const label = `tool:start  ${toolName}  toolUseId=${toolUseId}`;
      logGroup(label, {
        toolUseId,
        toolName,
        input
      });
      appendEntry({
        action: 'tool:start',
        label,
        conversationId: null,
        turnId: null,
        payload: {
          toolUseId,
          toolName,
          input
        }
      });
    },
    toolExecutionEnd(toolUseId, toolName, result, status) {
      const startTime = toolStartTimes.get(toolUseId);
      toolStartTimes.delete(toolUseId);
      const elapsed = startTime != null ? Math.round(performance.now() - startTime) : null;
      const elapsedLabel = elapsed != null ? `  ${elapsed}ms` : '';
      const label = `tool:end  ${toolName}  ${status}${elapsedLabel}`;
      logGroup(label, {
        toolUseId,
        toolName,
        result,
        status,
        elapsed
      });
      appendEntry({
        action: 'tool:end',
        label,
        conversationId: null,
        turnId: null,
        payload: {
          toolUseId,
          toolName,
          result,
          status,
          elapsed
        }
      });
    },
    conversationLoad(conversationId) {
      const label = `conversation:load  id=${conversationId}`;
      logGroup(label, {
        conversationId
      });
      appendEntry({
        action: 'conversation:load',
        label,
        conversationId,
        turnId: null,
        payload: {
          conversationId
        }
      });
    },
    conversationLoaded(conversationId, messageCount, hasMore) {
      const label = `conversation:loaded  id=${conversationId}  messages=${messageCount}  hasMore=${hasMore}`;
      logGroup(label, {
        conversationId,
        messageCount,
        hasMore
      });
      appendEntry({
        action: 'conversation:loaded',
        label,
        conversationId,
        turnId: null,
        payload: {
          conversationId,
          messageCount,
          hasMore
        }
      });
    },
    ablyConnectionSend(conversationId, ablyChannelId) {
      const hasChannel = ablyChannelId != null;
      const label = `ably:send  conversationId=${conversationId}  channelId=${ablyChannelId !== null && ablyChannelId !== void 0 ? ablyChannelId : 'NONE'}  willSubscribe=${hasChannel}`;
      logGroup(label, {
        conversationId,
        ablyChannelId,
        hasChannel
      });
      appendEntry({
        action: 'ably:send',
        label,
        conversationId,
        turnId: null,
        payload: {
          ablyChannelId,
          hasChannel
        }
      });
    },
    ablyEnsureConnected(currentState) {
      const label = `ably:ensure-connected  state=${currentState}`;
      logGroup(label, {
        currentState
      });
      appendEntry({
        action: 'ably:ensure-connected',
        label,
        conversationId: null,
        turnId: null,
        payload: {
          currentState
        }
      });
    },
    ablyConnected() {
      const label = 'ably:connected';
      logGroup(label, {});
      appendEntry({
        action: 'ably:connected',
        label,
        conversationId: null,
        turnId: null,
        payload: {}
      });
    },
    ablyConnectFailed(error) {
      const label = `ably:connect-failed  ${error}`;
      logGroup(label, {
        error
      });
      appendEntry({
        action: 'ably:connect-failed',
        label,
        conversationId: null,
        turnId: null,
        payload: {
          error
        }
      });
    },
    ablyDetachFailed(error) {
      const label = `ably:detach-failed  ${error}`;
      logGroup(label, {
        error
      });
      appendEntry({
        action: 'ably:detach-failed',
        label,
        conversationId: null,
        turnId: null,
        payload: {
          error
        }
      });
    },
    ablySubscribed(channelId) {
      const label = `ably:subscribed  channelId=${channelId}`;
      logGroup(label, {
        channelId
      });
      appendEntry({
        action: 'ably:subscribed',
        label,
        conversationId: null,
        turnId: null,
        payload: {
          channelId
        }
      });
    },
    ablyMessage(channelId, hasHandler) {
      const label = `ably:message  channelId=${channelId}  hasHandler=${hasHandler}`;
      logGroup(label, {
        channelId,
        hasHandler
      });
      appendEntry({
        action: 'ably:message',
        label,
        conversationId: null,
        turnId: null,
        payload: {
          channelId,
          hasHandler
        }
      });
    },
    clientDestroyed() {
      const label = 'client:destroyed';
      logGroup(label, {});
      appendEntry({
        action: 'client:destroyed',
        label,
        conversationId: null,
        turnId: null,
        payload: {}
      });
    },
    recordingStart: NOOP,
    streamingRawEvent(event) {
      const label = 'streaming:raw-event';
      logGroup(label, {
        event
      });
      appendEntry({
        action: 'streaming:raw-event',
        label,
        conversationId: null,
        turnId: null,
        payload: {
          event
        }
      });
    },
    streamingStreamEnd() {
      const label = 'streaming:stream-end';
      logGroup(label, {});
      appendEntry({
        action: 'streaming:stream-end',
        label,
        conversationId: null,
        turnId: null,
        payload: {}
      });
    }
  };
}
function createRecordingLogger(coordinator, initialKey, transportMode) {
  let activeRecording = null;
  let recordingStartMs = 0;
  let currentKey = initialKey !== null && initialKey !== void 0 ? initialKey : null;
  const api = {
    exportRecording() {
      if (!activeRecording) return null;
      return JSON.stringify(activeRecording, null, 2);
    },
    downloadRecording() {
      var _activeRecording$conv;
      if (!activeRecording) {
        console.warn('[breeze-engine] No active recording to download');
        return;
      }
      const json = JSON.stringify(activeRecording, null, 2);
      const blob = new Blob([json], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const convId = (_activeRecording$conv = activeRecording.conversationId) !== null && _activeRecording$conv !== void 0 ? _activeRecording$conv : 'new';
      const ts = activeRecording.recordedAt.replace(/[:.]/g, '-');
      a.href = url;
      a.download = `breeze-replay-${convId}-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    resetRecording() {
      activeRecording = null;
      recordingStartMs = Date.now();
    },
    hasRecording() {
      return activeRecording !== null;
    }
  };
  if (coordinator) {
    coordinator.register(currentKey, api);
  } else {
    _setRecordingCoordinator({
      exportRecording: () => api.exportRecording(),
      downloadRecording: () => api.downloadRecording(),
      resetRecording: () => api.resetRecording(),
      hasRecording: () => api.hasRecording()
    });
    initBreezeEngineWindow();
  }
  function offsetMs() {
    return Date.now() - recordingStartMs;
  }
  function appendEvent(event) {
    if (!activeRecording) return;
    activeRecording.events.push(event);
  }
  return {
    dispatch(action, _prevState, nextState) {
      if (!activeRecording) return;
      appendEvent({
        kind: 'dispatch',
        offsetMs: offsetMs(),
        action
      });
      if (activeRecording.conversationId === null && nextState.conversationId !== null) {
        activeRecording.conversationId = nextState.conversationId;
        if (coordinator) {
          coordinator.rekey(currentKey, nextState.conversationId);
          currentKey = nextState.conversationId;
        }
      }
    },
    dispatchNoop: NOOP,
    transportSend(request) {
      if (transportMode !== 'streaming') return;
      appendEvent({
        kind: 'transport:send',
        offsetMs: offsetMs(),
        request
      });
    },
    transportSendResponse(request, response) {
      if (transportMode === 'streaming') return;
      appendEvent({
        kind: 'transport:send',
        offsetMs: offsetMs(),
        request,
        response
      });
    },
    transportResume(request) {
      if (transportMode !== 'streaming') return;
      appendEvent({
        kind: 'transport:resume',
        offsetMs: offsetMs(),
        request
      });
    },
    transportResumeResponse(request, response) {
      if (transportMode === 'streaming') return;
      appendEvent({
        kind: 'transport:resume',
        offsetMs: offsetMs(),
        request,
        response
      });
    },
    transportToolResponse(request) {
      if (transportMode !== 'streaming') return;
      appendEvent({
        kind: 'transport:tool-response',
        offsetMs: offsetMs(),
        request,
        response: {}
      });
    },
    transportToolResponseResponse(request, response) {
      if (transportMode === 'streaming') return;
      appendEvent({
        kind: 'transport:tool-response',
        offsetMs: offsetMs(),
        request,
        response
      });
    },
    transportCancel(request) {
      appendEvent({
        kind: 'transport:cancel',
        offsetMs: offsetMs(),
        request
      });
    },
    transportStreamEvent: NOOP,
    ablyRawEvent(event) {
      if (transportMode === 'streaming') return;
      appendEvent({
        kind: 'ably:event',
        offsetMs: offsetMs(),
        payload: event
      });
    },
    transportReconnect: NOOP,
    transportRecovery: NOOP,
    transportPollCycle: NOOP,
    transportError(error) {
      appendEvent({
        kind: 'transport:error',
        offsetMs: offsetMs(),
        error
      });
    },
    toolExecutionStart: NOOP,
    toolExecutionEnd: NOOP,
    conversationLoad: NOOP,
    conversationLoaded: NOOP,
    ablyConnectionSend: NOOP,
    ablyEnsureConnected: NOOP,
    ablyConnected: NOOP,
    ablyConnectFailed: NOOP,
    ablyDetachFailed: NOOP,
    ablySubscribed: NOOP,
    ablyMessage: NOOP,
    clientDestroyed: NOOP,
    recordingStart(initialState) {
      recordingStartMs = Date.now();
      activeRecording = {
        version: 1,
        recordedAt: new Date().toISOString(),
        conversationId: initialState.conversationId,
        initialState: Object.assign({}, initialState),
        events: []
      };
    },
    streamingRawEvent(event) {
      appendEvent({
        kind: 'streaming:event',
        offsetMs: offsetMs(),
        payload: event
      });
    },
    streamingStreamEnd() {
      appendEvent({
        kind: 'streaming:end',
        offsetMs: offsetMs()
      });
    }
  };
}